import { buildChecklist, readinessSummary } from '../../readiness/rules.js';
import { estimate } from '../../core/estimate.js';
import { progress } from '../../intake/engine.js';
import { findConflicts } from '../../documents/reconcile.js';
import { operatingIncome } from '../../core/financials.js';
import { fmtMoney } from '../../intake/parseAnswer.js';
import { hasProgress, newId, nowIso, type Snapshot } from '../../workspace/schema.js';
import { Progress, Ribbons, StatusBadge } from '../components.js';
import { useWorkspace } from '../state.js';

function Landing() {
  return (
    <div className="stack-lg">
      <section className="hero">
        <Ribbons />
        <div className="eyebrow">Tyche Business · Financing assistant</div>
        <h1 tabIndex={-1}>Let&rsquo;s figure out your <span className="grad-text">next funding step.</span></h1>
        <p className="lead">
          Answer a few questions about your business. We&rsquo;ll organise your financing needs, find what you&rsquo;re
          missing, check your numbers, and prepare you for a conversation with a lender.
        </p>
        <div className="row" style={{ marginTop: 22 }}>
          <a className="btn primary" href="#/calculator">Calculate my loan — 2 minutes</a>
          <a className="btn" href="#/assistant">Guided walkthrough</a>
          <a className="btn ghost" href="#/model">How the estimate works</a>
        </div>
        <p className="small muted" style={{ marginTop: 18, marginBottom: 0 }}>
          Free and private: everything you enter or upload stays in this browser. Not a lender; no application is taken.
        </p>
      </section>

      <section aria-labelledby="how">
        <h2 id="how">From &ldquo;I need money&rdquo; to a plan you can take to a lender</h2>
        <div className="features">
          <div className="feature"><div className="k">01</div><h3>Start with the problem</h3><p>Tell the assistant what you want to fund. It explains unfamiliar terms in plain English.</p></div>
          <div className="feature"><div className="k">02</div><h3>Build a financing profile</h3><p>Only the questions that matter for your situation. Skip anything you don&rsquo;t know yet.</p></div>
          <div className="feature"><div className="k">03</div><h3>Find what&rsquo;s missing</h3><p>Upload statements and figures are read on your device, cited to the page, and checked against each other.</p></div>
          <div className="feature"><div className="k">04</div><h3>Leave with an action plan</h3><p>Your request, your numbers, programs to ask about, documents to bring and questions for the lender.</p></div>
        </div>
      </section>

      <section className="grid-2">
        <div className="card">
          <h3>Grounded in SBA&rsquo;s own data</h3>
          <p className="muted">
            The estimate is built from every SBA 7(a) loan SBA has published, back-tested on recent loans it never saw,
            and shows exactly which factors moved it.
          </p>
          <a href="#/model">See how it was tested</a>
        </div>
        <div className="card">
          <h3>Prepares you for SBA Lender Match</h3>
          <p className="muted">
            SBA&rsquo;s free Lender Match connects businesses with lenders. Tyche Business gets you ready for that
            conversation — it doesn&rsquo;t replace the lender or predict approval.
          </p>
          <a href="https://www.sba.gov/funding-programs/loans/lender-match" target="_blank" rel="noreferrer">About Lender Match</a>
        </div>
      </section>
    </div>
  );
}

const SNAP_FIELDS = ['annualRevenue', 'operatingIncome', 'existingAnnualDebtService', 'cash'] as const;
const SNAP_LABELS: Record<(typeof SNAP_FIELDS)[number] | 'amount', string> = {
  annualRevenue: 'Revenue',
  operatingIncome: 'Operating income',
  existingAnnualDebtService: 'Loan payments / yr',
  cash: 'Cash',
  amount: 'Amount sought',
};

function Dashboard() {
  const { ws, update } = useWorkspace();
  const items = buildChecklist(ws);
  const sum = readinessSummary(items);
  const intake = progress(ws);
  const est = estimate(ws);
  const conflicts = findConflicts(ws).filter((c) => !c.resolved);
  const pending = ws.documents.flatMap((d) => d.candidates).filter((c) => c.state === 'pending').length;
  const next = items.filter((i) => i.status !== 'ready' && i.importance === 'likely').slice(0, 4);
  const oi = operatingIncome(ws);

  const takeSnapshot = () => update((w) => {
    const figures: Snapshot['figures'] = {};
    for (const f of SNAP_FIELDS) {
      const v = f === 'operatingIncome' ? oi?.value : w.profile[f]?.value;
      if (v !== undefined) figures[f] = v;
    }
    if (w.profile.amount) figures.amount = w.profile.amount.value;
    const at = nowIso();
    return { ...w, snapshots: [...w.snapshots, { id: newId('snap'), at, label: new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), figures }] };
  });

  return (
    <div className="stack-lg">
      <section className="hero" style={{ padding: 'clamp(22px, 4vw, 40px)' }}>
        <Ribbons />
        <div className="eyebrow">Your workspace</div>
        <h1 tabIndex={-1} style={{ maxWidth: 'none' }}>Welcome back.</h1>
        <div className="grid-3" style={{ marginTop: 18 }}>
          <div className="stat">
            <span className="label">Readiness</span>
            <span className="big-number">{sum.ready}<span className="faint" style={{ fontSize: '0.5em' }}> / {sum.total}</span></span>
            <Progress value={sum.ready} max={sum.total} label="Checklist items ready" />
          </div>
          <div className="stat">
            <span className="label">Profile questions</span>
            <span className="big-number">{intake.done}<span className="faint" style={{ fontSize: '0.5em' }}> / {intake.total}</span></span>
            <Progress value={intake.done} max={intake.total} label="Profile questions answered" />
          </div>
          <div className="stat">
            <span className="label">Estimate</span>
            {est.ready
              ? <><span className="big-number" style={{ fontSize: '1.4rem' }}>{est.result.bandLabel}</span><a href="#/estimate">See why</a></>
              : <><span className="muted">Needs {est.missing.join(', ')}</span><a href="#/assistant">Continue the assistant</a></>}
          </div>
        </div>
      </section>

      <div className="split">
        <section className="card">
          <div className="card-title"><h2>Next steps</h2><a href="#/readiness">Full checklist</a></div>
          {next.length === 0
            ? <p className="muted">Everything lenders commonly ask for is in place. Review your <a href="#/plan">action plan</a>.</p>
            : (
              <ol className="steps">
                {next.map((i) => (
                  <li key={i.id}>
                    <div>
                      <div className="spread"><strong>{i.title}</strong><StatusBadge status={i.status} /></div>
                      <div className="small muted">{i.missing}</div>
                      {i.route && <a className="small" href={i.route}>Work on this</a>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
        </section>

        <aside className="stack">
          <div className="card">
            <h3>Documents</h3>
            <p className="muted" style={{ marginBottom: 8 }}>
              {ws.documents.length === 0 ? 'None uploaded yet.' : `${ws.documents.length} uploaded.`}
              {pending > 0 && ` ${pending} figure${pending === 1 ? '' : 's'} to confirm.`}
            </p>
            {conflicts.length > 0 && <p className="callout warn small">{conflicts.length} figure{conflicts.length === 1 ? '' : 's'} differ between documents.</p>}
            <a href="#/documents">{ws.documents.length ? 'Review documents' : 'Upload documents'}</a>
          </div>
          <div className="card">
            <h3>Action plan</h3>
            <p className="muted">Your request, numbers, missing items and lender questions in one printable page.</p>
            <a className="btn small primary" href="#/plan">Open action plan</a>
          </div>
        </aside>
      </div>

      <section className="card">
        <div className="card-title">
          <h2>How your numbers have changed</h2>
          <button className="btn small" type="button" onClick={takeSnapshot}>Save today&rsquo;s figures</button>
        </div>
        <p className="muted small">
          Save a snapshot whenever you update your financials, so you can see your position change as the business grows
          and revisit funding when you&rsquo;re ready.
        </p>
        {ws.snapshots.length === 0
          ? <p className="faint">No snapshots yet.</p>
          : (
            <div className="table-scroll">
              <table className="table">
                <thead><tr><th>Figure</th>{ws.snapshots.map((s) => <th key={s.id} className="r">{s.label}</th>)}</tr></thead>
                <tbody>
                  {(['amount', ...SNAP_FIELDS] as const).map((f) => (
                    <tr key={f}>
                      <td>{SNAP_LABELS[f]}</td>
                      {ws.snapshots.map((s) => <td key={s.id} className="r">{s.figures[f] !== undefined ? fmtMoney(s.figures[f]!) : '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>
    </div>
  );
}

export function Home() {
  const { ws } = useWorkspace();
  return hasProgress(ws) ? <Dashboard /> : <Landing />;
}
